import React, { useContext, useEffect } from "react";
import {
  FoundersType,
  ResultContext,
  ShareholdersType,
  SharesType,
} from "../ResultContext";
import { GivenQuestions } from "../constants/Questions";
import { DecimalInput } from "./DecimalInput";
import { Label } from "../components/Label";

export function SharesInput({ questionId }: { questionId: number }) {
  const { result, setResult } = useContext(ResultContext);

  const currentQuestion = GivenQuestions.find((q) => q.id === questionId) as {
    dependsOn: number;
  };

  const dependsOn = currentQuestion.dependsOn as number;

  const dependsOnResult =
    (result[dependsOn] as {
      name: string;
      type: FoundersType;
    }[]) || [];

  const equalShares = (100 / dependsOnResult.length).toFixed(2);

  const foundResult = (result[questionId] as ShareholdersType) || {
    type: SharesType.Percentage,
    externalShareholders: { external: false },
    shares: Array.from({ length: dependsOnResult.length }, () => {
      return { share: "" };
    }),
  };

  useEffect(() => {
    if (!result[questionId]) {
      const copy = { ...result };

      copy[questionId] = {
        type: SharesType.Percentage,
        shares: Array.from({ length: dependsOnResult.length }, () => {
          return {
            share: "",
          };
        }),
        externalShareholders: { external: false },
      };

      setResult(copy);
    }
    // eslint-disable-next-line
  }, [questionId]);

  if (dependsOnResult.length > foundResult.shares.length) {
    const diff = dependsOnResult.length - foundResult.shares.length;
    for (let i = 0; i < diff; i++) {
      foundResult.shares.push({
        share: "",
      });
    }
  }

  let sharesSum = 0;
  if (foundResult.externalShareholders.external) {
    sharesSum =
      foundResult.shares.reduce((a, b) => a + parseFloat(b.share), 0) +
      parseFloat(foundResult.externalShareholders.share);
  } else {
    sharesSum = foundResult.shares.reduce((a, b) => a + parseFloat(b.share), 0);
  }

  return (
    <div className="flex flex-column">
      <Label
        title={"Percentage or # shares" || ""}
        information={
          <>
            <div className="text-justify">
              Preferably we work with exact amount of shares as this is exact
              math; however, if the exact amount of shares are not known, we
              have provided the option to work with % (which we will round).
            </div>
            <div className="text-justify mt-4">
              When founders decide on the allocation of equity amongst each
              other (share-split), different parameters can be taken into
              account. In this first version of the tool we only ask you about
              your time-commitment but some other important parameters can be:
              contributions (financial, intellectual property or time already
              spent in the past), expertise, etc.
            </div>
          </>
        }
      />
      <select
        value={foundResult.type}
        onChange={(e) => {
          const copy = { ...result };

          copy[questionId] = {
            ...foundResult,
            type: e.target.value as SharesType,
          };

          setResult(copy);
        }}
      >
        {Object.values(SharesType).map((item, i) => {
          return (
            <option value={item} key={i}>
              {item}
            </option>
          );
        })}
      </select>
      <div className="sub-question">Founders</div>

      {dependsOnResult.length === 1 && dependsOnResult[0].name === ""
        ? null
        : dependsOnResult.map((item, index) => {
            return (
              <div className={`${index !== 0 ? "mt-4" : ""}`} key={index}>
                <label>{item.name}</label>
                <div className="flex items-center">
                  <DecimalInput
                    min={foundResult.type === SharesType.Percentage ? 0 : false}
                    max={
                      foundResult.type === SharesType.Percentage ? 100 : false
                    }
                    value={foundResult.shares[index].share}
                    placeholder={"Shares"}
                    percentage={
                      foundResult.type === SharesType.Percentage ? true : false
                    }
                    onChange={(e) => {
                      const copy = { ...result };

                      copy[questionId] = {
                        ...foundResult,
                        shares: foundResult.shares.map((it, id) => {
                          if (id === index) {
                            return {
                              ...it,
                              share: e,
                            };
                          }
                          return it;
                        }),
                      };

                      setResult(copy);
                    }}
                  />
                </div>
              </div>
            );
          })}

      <Label
        title={"External shareholders"}
        information={
          <>
            <div className="text-justify">
              Note that in this version of the tool it is not possible to
              include a vesting schedule for an external shareholder. If this
              would be required, this shareholder should be marked as founder.
            </div>
          </>
        }
        type={"subQuestion"}
      />
      <div className="flex align-items-end">
        <select
          value={foundResult.externalShareholders.external ? "yes" : "no"}
          onChange={(e) => {
            const copy = { ...result };

            if (e.target.value === "yes") {
              copy[questionId] = {
                ...foundResult,
                externalShareholders: { external: true, share: "" },
              };
            } else {
              copy[questionId] = {
                ...foundResult,
                externalShareholders: { external: false },
              };
            }

            setResult(copy);
          }}
        >
          <option value="no">No</option>
          <option value="yes">Yes</option>
        </select>
        <div className={"ml-4"} />
        {foundResult.externalShareholders.external ? (
          <div>
            <label> Shares </label>
            <DecimalInput
              min={foundResult.type === SharesType.Percentage ? 0 : false}
              max={foundResult.type === SharesType.Percentage ? 100 : false}
              percentage={
                foundResult.type === SharesType.Percentage ? true : false
              }
              value={foundResult.externalShareholders.share}
              placeholder={"Shares"}
              onChange={(e) => {
                const copy = { ...result };

                copy[questionId] = {
                  ...foundResult,
                  externalShareholders: {
                    external: true,
                    share: e,
                  },
                };

                setResult(copy);
              }}
            />
          </div>
        ) : null}
      </div>
      {foundResult.type === SharesType.Percentage && sharesSum !== 100 ? (
        <div className="red">Total shares is not equal to 100%</div>
      ) : null}
    </div>
  );
}
